import { htmlHasText } from "../../../../utils/htmlText";

export type ReadinessCheck = {
  id: string;
  label: string;
  complete: boolean;
  missingMessage: string;
  targetId: string;
};

export type ReadinessArea = {
  id: string;
  label: string;
  checks: ReadinessCheck[];
  complete: boolean;
  incompleteCount: number;
  completeSummary: string;
};

export type SetupReadinessModel = {
  areas: ReadinessArea[];
  completedAreaCount: number;
  incompleteAreaCount: number;
  areaPercentage: number;
  readyForReview: boolean;
};

type ScheduleDay = { date: string; from: string; to: string };

function hasText(value: string) {
  return value.trim().length > 0;
}

function buildArea(
  id: string,
  label: string,
  checks: ReadinessCheck[],
  completeSummary = "Complete",
): ReadinessArea {
  const incompleteCount = checks.filter((c) => !c.complete).length;
  return {
    id,
    label,
    checks,
    complete: incompleteCount === 0,
    incompleteCount,
    completeSummary,
  };
}

function scheduleCheck(
  day: "friday" | "saturday" | "sunday",
  field: "date" | "from" | "to",
  schedule: Record<"friday" | "saturday" | "sunday", ScheduleDay>,
): ReadinessCheck {
  const dayLabel = day[0].toUpperCase() + day.slice(1);
  const fieldLabel = field === "date" ? "date" : `${field} time`;
  return {
    id: `${day}-${field}`,
    label: `${dayLabel} ${fieldLabel}`,
    complete: hasText(schedule[day][field]),
    missingMessage: `${dayLabel} ${fieldLabel} is missing`,
    targetId: `setup-field-${day}`,
  };
}

function formatScheduleSummary(schedule: Record<"friday" | "saturday" | "sunday", ScheduleDay>) {
  const friday = schedule.friday.date;
  const sunday = schedule.sunday.date;
  if (!friday || !sunday) return "Weekend dates complete";
  const start = new Date(`${friday}T12:00:00`);
  const end = new Date(`${sunday}T12:00:00`);
  const fmt = (d: Date, withYear = false) =>
    d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      ...(withYear ? { year: "numeric" } : {}),
    });
  return `${fmt(start)} – ${fmt(end, true)}`;
}

export function computeSetupReadinessAreas(input: {
  themeTitle: string;
  themeImage: string;
  missionStatement: string;
  retreatDetails: string;
  emailMessage: string;
  paymentLink: string;
  schedule: Record<"friday" | "saturday" | "sunday", ScheduleDay>;
  leadership: {
    leadCoupleId: number | "";
    coLeadCoupleId: number | "";
    mentorCoupleId: number | "";
  };
  rtpAssigned: boolean;
}): SetupReadinessModel {
  const areas: ReadinessArea[] = [
    buildArea("identity", "Retreat Identity", [
      {
        id: "theme-title",
        label: "Retreat Theme / Title",
        complete: hasText(input.themeTitle),
        missingMessage: "Retreat Theme / Title is missing",
        targetId: "setup-field-theme-title",
      },
      {
        id: "theme-image",
        label: "Retreat Theme Image",
        complete: Boolean(input.themeImage),
        missingMessage: "Theme image needed",
        targetId: "setup-field-theme-image",
      },
    ]),
    buildArea(
      "schedule",
      "Retreat Schedule",
      (["friday", "saturday", "sunday"] as const).flatMap((day) =>
        (["date", "from", "to"] as const).map((field) =>
          scheduleCheck(day, field, input.schedule),
        ),
      ),
      formatScheduleSummary(input.schedule),
    ),
    buildArea("public-information", "Public Retreat Information", [
      {
        id: "mission",
        label: "Mission Statement",
        complete: htmlHasText(input.missionStatement),
        missingMessage: "Mission Statement is missing",
        targetId: "setup-field-mission",
      },
      {
        id: "details",
        label: "Retreat Details",
        complete: htmlHasText(input.retreatDetails),
        missingMessage: "Retreat Details are missing",
        targetId: "setup-field-details",
      },
    ]),
    buildArea("registration-communication", "Registration Confirmation Email", [
      {
        id: "email",
        label: "Registration Confirmation Email",
        complete: htmlHasText(input.emailMessage),
        missingMessage: "Registration Confirmation Email is incomplete",
        targetId: "setup-field-email",
      },
    ]),
    buildArea("payment", "Registration Payment Setup", [
      {
        id: "payment",
        label: "Registration Payment Link",
        complete: hasText(input.paymentLink),
        missingMessage: "Registration payment link is missing",
        targetId: "setup-field-payment",
      },
    ]),
    buildArea("leadership", "Retreat Leadership", [
      {
        id: "lead",
        label: "Lead Couple",
        complete: Boolean(input.leadership.leadCoupleId),
        missingMessage: "Lead Couple is missing",
        targetId: "setup-field-lead",
      },
      {
        id: "coLead",
        label: "Co-Lead Couple",
        complete: Boolean(input.leadership.coLeadCoupleId),
        missingMessage: "Co-Lead Couple is missing",
        targetId: "setup-field-coLead",
      },
      {
        id: "mentor",
        label: "Mentor Couple",
        complete: Boolean(input.leadership.mentorCoupleId),
        missingMessage: "Mentor Couple is missing",
        targetId: "setup-field-mentor",
      },
    ]),
    buildArea("rtp", "Registration Administration — RTP", [
      {
        id: "rtp",
        label: "RTP Registration Administrator",
        complete: input.rtpAssigned,
        missingMessage: "RTP Registration Administrator required",
        targetId: "setup-field-rtp",
      },
    ]),
  ];

  const completedAreaCount = areas.filter((a) => a.complete).length;
  const incompleteAreaCount = areas.length - completedAreaCount;
  const areaPercentage = Math.round((completedAreaCount / areas.length) * 100);

  return {
    areas,
    completedAreaCount,
    incompleteAreaCount,
    areaPercentage,
    readyForReview: incompleteAreaCount === 0,
  };
}
